import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes,
  faExternalLinkAlt,
  faCertificate,
  faUserTie,
  faCalendarAlt,
  faAward
} from '@fortawesome/free-solid-svg-icons';
import certificatesData from "../data/certificate.json";
import '../styles/certificate.css';

const Certificate = () => {
  const [activeTab, setActiveTab] = useState('certifications');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const { certifications, recommendations } = certificatesData;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleClick = (item) => {
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  return (
    <div className="certificate-container">
      <div className="section-header-container">
        <div className="header-icon-container">
          <FontAwesomeIcon icon={faAward} className="header-icon" />
        </div>
        <h1 className="section-header">
          Professional Credentials
        </h1>
        <p className="section-description">
          My verified certifications and professional recommendations that showcase my skills and expertise.
        </p>
      </div>
      
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'certifications' ? 'active' : ''}`}
          onClick={() => handleTabChange('certifications')}
        >
          <FontAwesomeIcon icon={faCertificate} />
          Certifications
        </button>
        <button 
          className={`tab-btn ${activeTab === 'recommendations' ? 'active' : ''}`}
          onClick={() => handleTabChange('recommendations')}
        >
          <FontAwesomeIcon icon={faUserTie} />
          Recommendations
        </button>
      </div>

      <div className="content-grid">
        {activeTab === 'certifications' ? (
          certifications.map((item, index) => (
            <CertificationCard 
              key={index} 
              item={item} 
              onClick={() => handleClick(item)} 
            />
          ))
        ) : (
          recommendations.map((item, index) => (
            <RecommendationCard 
              key={index} 
              item={item} 
              onClick={() => handleClick(item)} 
            />
          ))
        )}
      </div>

      {isModalOpen && currentItem && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={handleCloseModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            {activeTab === 'certifications' ? (
              <CertificationModal item={currentItem} />
            ) : (
              <RecommendationModal item={currentItem} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Certification Card Component
const CertificationCard = ({ item, onClick }) => (
  <div className="card certification-card" onClick={onClick}>
    <div className="card-badge">
      <FontAwesomeIcon icon={faCertificate} />
    </div>
    <div className="card-img-container">
      <img src={item.image} alt={item.name} />
    </div>
    <div className="card-content">
      <h3>{item.name}</h3>
      <p className="issuer">{item.issuer}</p>
      <p className="date">
        <FontAwesomeIcon icon={faCalendarAlt} />
        {item.issuedDate}
      </p>
    </div>
  </div>
);

// Recommendation Card Component
const RecommendationCard = ({ item, onClick }) => (
  <div className="card recommendation-card" onClick={onClick}>
    <div className="card-badge">
      <FontAwesomeIcon icon={faUserTie} />
    </div>
    <div className="recommender-avatar-container">
      <img 
        src={item.recommenderImage} 
        alt={item.recommender} 
        className="recommender-avatar" 
      />
    </div>
    <div className="card-content">
      <h3>{item.recommender}</h3>
      <p className="position">{item.recommenderTitle}</p>
      <p className="excerpt">"{item.excerpt}..."</p>
    </div>
  </div>
);

// Certification Modal Component
const CertificationModal = ({ item }) => (
  <>
    <div className="modal-header">
      <div className="modal-badge">
        <FontAwesomeIcon icon={faCertificate} />
      </div>
      <h2>{item.name}</h2>
      <p className="issuer">{item.issuer}</p>
    </div>
    
    <div className="modal-img-container">
      <img src={item.image} alt={item.name} className="certificate-image" />
    </div>
    
    <div className="modal-details">
      <div className="detail-row">
        <span className="detail-label">Issued Date:</span>
        <span className="detail-value">{item.issuedDate}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Description:</span>
        <p className="detail-value">{item.description}</p>
      </div>
      {item.skills && (
        <div className="detail-row">
          <span className="detail-label">Skills:</span>
          <div className="skills-container">
            {item.skills.map((skill, index) => (
              <span key={index} className="skill-tag">{skill}</span>
            ))}
          </div>
        </div>
      )}
    </div>
    
    <a 
      href={item.credentialLink} 
      className="verify-link" 
      target="_blank" 
      rel="noopener noreferrer"
    >
      <FontAwesomeIcon icon={faExternalLinkAlt} />
      Verify Credential
    </a>
  </>
);

// Recommendation Modal Component
const RecommendationModal = ({ item }) => (
  <>
    <div className="modal-header">
      <div className="modal-badge">
        <FontAwesomeIcon icon={faUserTie} />
      </div>
      <img 
        src={item.recommenderImage} 
        alt={item.recommender} 
        className="modal-avatar" 
      />
      <div>
        <h2>{item.recommender}</h2>
        <p className="position">{item.recommenderTitle} at {item.recommenderCompany}</p>
      </div>
    </div>
    
    <div className="recommendation-content">
      <blockquote>"{item.content}"</blockquote>
      <div className="recommendation-meta">
        <p className="connection">
          <strong>Connection:</strong> {item.connection}
        </p>
        <p className="date">
          <FontAwesomeIcon icon={faCalendarAlt} />
          {item.date}
        </p>
      </div>
    </div>
    
    <a 
      href={item.linkedInUrl} 
      className="verify-link recommendation-verify-link" 
      target="_blank" 
      rel="noopener noreferrer"
    >
      <FontAwesomeIcon icon={faExternalLinkAlt} />
      View
    </a>
  </>
);

export default Certificate;